//Tutorial Modal.tsx - Updated with Multilingual CSV Support
/* eslint-disable @typescript-eslint/dot-notation, object-shorthand, @typescript-eslint/prefer-for-of */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import '../../../../../Assets/styles/LandingStyles/LandingComponentsStyles/Tutorial/TutorialModal.css';
import { ReactComponent as BookSvg } from '../../../../../Assets/svgs/book-open-solid.svg';
import { ReactComponent as GamePadSvg } from '../../../../../Assets/svgs/gamepad-solid.svg';
import { ReactComponent as HandshakeSvg } from '../../../../../Assets/svgs/handshake-angle-solid.svg';
import { ReactComponent as RocketSvg } from '../../../../../Assets/svgs/rocket-solid.svg';
import { useLanguage } from '../../../../../Core/Translations/LanguageContext';
import { LanguageSelector } from '../../../../../Core/Translations/LanguageSelector';

interface TutorialsModalProps {
    onClose: () => void;
    csvUrl?: string; // Now single CSV URL instead of language-specific
}

interface TutorialVideo {
    id: string;
    title: string;
    videoUrl: string;
    tab: string;
    // Store all language titles
    titles: {
        [key: string]: string;
    };
}

// Default CSV URL - single CSV with all language titles
const DEFAULT_CSV_URL = "https://docs.google.com/spreadsheets/d/e/2PACX-1vSBnY7sO-rjPKU4TTPd3ux3ppUeHtJ0W0Y7_xZjbkYjCa4Fys0BZAF2e1vA0AFdJ-zsGeQJ-sQ_exHu/pub?gid=0&single=true&output=csv";

// Supported languages list for parsing CSV columns
const SUPPORTED_LANGUAGES = ['en', 'es', 'fr', 'hi', 'bn', 'pt', 'it', 'ru', 'ja', 'ko'];

export const TutorialsModal: React.FC<TutorialsModalProps> = ({
    onClose,
    csvUrl = DEFAULT_CSV_URL
}) => {
    const { t, language } = useLanguage();
    const [searchParams, setSearchParams] = useSearchParams();
    const urlTab = searchParams.get('tab') as typeof activeTab;

    const [activeTab, setActiveTab] = useState<'introduction' | 'getting-started' | 'vs-friends' | 'how-to-play'>(
        ['introduction', 'getting-started', 'vs-friends', 'how-to-play'].includes(urlTab)
            ? urlTab
            : 'introduction'
    );

    const [tutorialVideos, setTutorialVideos] = useState<Record<string, TutorialVideo[]>>({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Fetch and parse CSV data - now handles multilingual titles
    useEffect(() => {
        const fetchTutorialData = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const response = await fetch(csvUrl);
                if (!response.ok) {
                    throw new Error(`Failed to fetch CSV: ${response.statusText}`);
                }

                const csvText = await response.text();
                const parsedData = parseMultilingualCSV(csvText);

                // Group videos by tab
                const groupedVideos = parsedData.reduce((acc, video) => {
                    const tab = video.tab.toLowerCase().replace(/\s+/g, '-');
                    if (!acc[tab]) {
                        acc[tab] = [];
                    }
                    acc[tab].push(video);
                    return acc;
                }, {} as Record<string, TutorialVideo[]>);

                setTutorialVideos(groupedVideos);
            } catch (err) {
                console.error('Error fetching tutorial data:', err);
                setError(err instanceof Error ? err.message : 'Failed to load tutorial data');

                // Fallback data with multilingual support
                const fallbackData = getFallbackData();
                setTutorialVideos(fallbackData);
            } finally {
                setIsLoading(false);
            }
        };

        fetchTutorialData();
    }, [csvUrl]);

// Parse CSV with multilingual title columns - FIXED VERSION
const parseMultilingualCSV = (csvText: string): TutorialVideo[] => {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) return [];

    // Parse headers to identify title columns
    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, '').toLowerCase());
    const videos: TutorialVideo[] = [];

    // Find indices for each column we need
    const idIndex = headers.indexOf('id');
    const videoUrlIndex = headers.indexOf('videourl');
    const categoryIndex = headers.indexOf('category');
    
    // Find all title column indices
    const titleIndices: { [lang: string]: number } = {};
    SUPPORTED_LANGUAGES.forEach(lang => {
        const index = headers.indexOf(`title_${lang}`);
        if (index !== -1) {
            titleIndices[lang] = index;
        }
    });

    // If no language-specific columns found, fall back to single title column
    const singleTitleIndex = headers.indexOf('title');
    const hasMultilingualTitles = Object.keys(titleIndices).length > 0;

    for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i]);
        
        // Skip empty or invalid rows
        if (values.length <= Math.max(idIndex, videoUrlIndex, categoryIndex) || 
            !values[idIndex] || 
            !values[videoUrlIndex] || 
            !values[categoryIndex] ||
            values[idIndex].trim() === '' ||
            values[videoUrlIndex].trim() === '' ||
            values[categoryIndex].trim() === '') {
            continue; // Skip this row
        }

        const titles: { [key: string]: string } = {};
        
        if (hasMultilingualTitles) {
            // Use language-specific titles
            Object.entries(titleIndices).forEach(([lang, index]) => {
                if (values[index] && values[index].trim() !== '') {
                    titles[lang] = values[index];
                }
            });
            
            // Skip if no valid titles found
            if (Object.keys(titles).length === 0) {
                continue;
            }
        } else if (singleTitleIndex !== -1) {
            // Fall back to single title for all languages
            const singleTitle = values[singleTitleIndex] || '';
            if (singleTitle.trim() === '') {
                continue; // Skip if no title
            }
            SUPPORTED_LANGUAGES.forEach(lang => {
                titles[lang] = singleTitle;
            });
        }

        // Get the title for the current language, fallback to English if not found
        const currentTitle = titles[language] || titles['en'] || Object.values(titles)[0] || '';
        
        // Final check - skip if no valid title
        if (currentTitle.trim() === '') {
            continue;
        }

        videos.push({
            id: values[idIndex].trim(),
            title: currentTitle,
            videoUrl: values[videoUrlIndex].trim(),
            tab: values[categoryIndex].trim(),
            titles: titles
        });
    }

    return videos;
};

    // Parse a single CSV line handling quoted values
    const parseCSVLine = (line: string): string[] => {
        const result = [];
        let current = '';
        let inQuotes = false;

        for (let i = 0; i < line.length; i++) {
            const char = line[i];

            if (char === '"') {
                inQuotes = !inQuotes;
            } else if (char === ',' && !inQuotes) {
                result.push(current.trim());
                current = '';
            } else {
                current += char;
            }
        }

        result.push(current.trim());
        return result.map(val => val.replace(/^"|"$/g, ''));
    };

    // Fallback data with multilingual support
    const getFallbackData = () => {
        const fallbackVideos = [
            {
                id: 'intro-1',
                titles: {
                    en: 'SVO Home',
                    es: 'Inicio de SVO',
                    fr: 'Accueil SVO',
                    hi: 'SVO होम',
                    bn: 'SVO হোম',
                    pt: 'Início SVO',
                    it: 'Home SVO',
                    ru: 'Главная SVO',
                    ja: 'SVOホーム',
                    ko: 'SVO 홈'
                },
                videoUrl: 'https://youtube.com/shorts/nid_zGhlJzo?feature=share',
                tab: 'introduction'
            },
            {
                id: 'gs-1',
                titles: {
                    en: 'Vs. Match Explainer',
                    es: 'Explicación de Partida Vs.',
                    fr: 'Explication Match Vs.',
                    hi: 'Vs. मैच व्याख्या',
                    bn: 'Vs. ম্যাচ ব্যাখ্যা',
                    pt: 'Explicação Partida Vs.',
                    it: 'Spiegazione Partita Vs.',
                    ru: 'Объяснение матча Vs.',
                    ja: 'Vs.マッチ説明',
                    ko: 'Vs. 매치 설명'
                },
                videoUrl: 'https://youtube.com/shorts/cY9JlIfHUDs',
                tab: 'getting-started'
            },
            {
                id: 'gs-2',
                titles: {
                    en: 'How to Start a Vs. Game',
                    es: 'Cómo Iniciar un Juego Vs.',
                    fr: 'Comment Démarrer un Jeu Vs.',
                    hi: 'Vs. गेम कैसे शुरू करें',
                    bn: 'Vs. গেম কিভাবে শুরু করবেন',
                    pt: 'Como Iniciar um Jogo Vs.',
                    it: 'Come Iniziare una Partita Vs.',
                    ru: 'Как начать игру Vs.',
                    ja: 'Vs.ゲームの始め方',
                    ko: 'Vs. 게임 시작 방법'
                },
                videoUrl: 'https://youtube.com/shorts/UzLm-02Yyvg?feature=share',
                tab: 'getting-started'
            },
            {
                id: 'vs-1',
                titles: {
                    en: 'How to Add a Friend',
                    es: 'Cómo Agregar un Amigo',
                    fr: 'Comment Ajouter un Ami',
                    hi: 'दोस्त कैसे जोड़ें',
                    bn: 'কিভাবে বন্ধু যোগ করবেন',
                    pt: 'Como Adicionar um Amigo',
                    it: 'Come Aggiungere un Amico',
                    ru: 'Как добавить друга',
                    ja: '友達を追加する方法',
                    ko: '친구 추가 방법'
                },
                videoUrl: 'https://youtube.com/shorts/oC8sKhsXmCw?feature=share',
                tab: 'vs-friends'
            },
            {
                id: 'vs-2',
                titles: {
                    en: 'How to Challenge a Friend',
                    es: 'Cómo Desafiar a un Amigo',
                    fr: 'Comment Défier un Ami',
                    hi: 'दोस्त को चुनौती कैसे दें',
                    bn: 'কিভাবে বন্ধুকে চ্যালেঞ্জ করবেন',
                    pt: 'Como Desafiar um Amigo',
                    it: 'Come Sfidare un Amico',
                    ru: 'Как бросить вызов другу',
                    ja: '友達に挑戦する方法',
                    ko: '친구에게 도전하는 방법'
                },
                videoUrl: 'https://youtube.com/shorts/SShTc0dhZkk?feature=share',
                tab: 'vs-friends'
            },
            {
                id: 'htp-1',
                titles: {
                    en: 'SVO Single Mode',
                    es: 'Modo Individual SVO',
                    fr: 'Mode Solo SVO',
                    hi: 'SVO सिंगल मोड',
                    bn: 'SVO একক মোড',
                    pt: 'Modo Individual SVO',
                    it: 'Modalità Singola SVO',
                    ru: 'Одиночный режим SVO',
                    ja: 'SVOシングルモード',
                    ko: 'SVO 싱글 모드'
                },
                videoUrl: 'https://youtube.com/shorts/IfRIIqNaX9s?feature=share',
                tab: 'how-to-play'
            },
            {
                id: 'htp-2',
                titles: {
                    en: 'SV Strategy',
                    es: 'Estrategia SV',
                    fr: 'Stratégie SV',
                    hi: 'SV रणनीति',
                    bn: 'SV কৌশল',
                    pt: 'Estratégia SV',
                    it: 'Strategia SV',
                    ru: 'Стратегия SV',
                    ja: 'SV戦略',
                    ko: 'SV 전략'
                },
                videoUrl: 'https://youtube.com/shorts/GuUt-V87JDY?si=QwKLGv8b2h1V9WpJ',
                tab: 'how-to-play'
            }
        ];

        // Convert to grouped format with current language title
        const grouped: Record<string, TutorialVideo[]> = {};
        fallbackVideos.forEach(video => {
            const currentTitle = video.titles[language] || video.titles['en'];
            const formattedVideo = {
                ...video,
                title: currentTitle
            };
            
            if (!grouped[video.tab]) {
                grouped[video.tab] = [];
            }
            grouped[video.tab].push(formattedVideo);
        });

        return grouped;
    };

    // Update videos when language changes
    useEffect(() => {
        // Re-map videos with new language titles
        const updatedVideos: Record<string, TutorialVideo[]> = {};
        
        Object.entries(tutorialVideos).forEach(([tab, videos]) => {
            updatedVideos[tab] = videos.map(video => ({
                ...video,
                title: video.titles?.[language] || video.titles?.['en'] || video.title
            }));
        });

        if (Object.keys(updatedVideos).length > 0) {
            setTutorialVideos(updatedVideos);
        }
    }, [language]);

    // Update URL when tab changes
    const handleTabChange = (tab: typeof activeTab) => {
        setActiveTab(tab);
        setSearchParams({ tab });
    };

    // Sync with browser navigation
    useEffect(() => {
        if (urlTab && urlTab !== activeTab) {
            setActiveTab(urlTab);
        }
    }, [urlTab]);

    // Helper function to extract YouTube video ID from various URL formats
    const extractYouTubeId = (url: string): string => {
        if (url.includes('youtube.com/shorts/')) {
            const idMatch = url.match(/youtube\.com\/shorts\/([^?&]+)/);
            return idMatch ? idMatch[1] : '';
        }

        const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
        return match ? match[1] : '';
    };

    // Handler to close modal and clear params
    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            setSearchParams({});
            onClose();
        }
    };

    // Render tutorial videos for the current tab in a grid layout
    const renderTutorialVideos = () => {
        if (isLoading) {
            return (
                <div className="tutorials-loading">
                    <div>{t.tutorials.messages.loading}</div>
                </div>
            );
        }

        if (error) {
            return (
                <div className="tutorials-error">
                    <div>{t.tutorials.messages.error} {error}</div>
                    <div>{t.tutorials.messages.fallback}</div>
                </div>
            );
        }

        const videos = tutorialVideos[activeTab] || [];

        if (videos.length === 0) {
            return (
                <div className="tutorials-empty">
                    <div>{t.tutorials.messages.empty}</div>
                </div>
            );
        }

        return (
            <div className="tutorials-video-grid">
                {videos.map(video => {
                    const videoId = extractYouTubeId(video.videoUrl);

                    return (
                        <div key={video.id} className="tutorials-video-container">
                            <div className="tutorials-video-placeholder">
                                <iframe
                                    src={`https://www.youtube.com/embed/${videoId}`}
                                    title={video.title}
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    className="tutorials-video-iframe"
                                />
                                <div className="tutorials-video-title">{video.title}</div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="tutorials-modal-overlay" onClick={handleOverlayClick}>
            <div className="tutorials-modal-container tutorials-modal">
                {/* Header container */}
            <div className="tutorials-modal-header">
                <div className="tutorials-modal-title">
                    {t.tutorials.modalTitle}
                    <div style={{ marginLeft: 'auto', marginRight: '30px' }}>
                        <LanguageSelector theme="light" dropdownDirection='down' />
                    </div>
                    
                    <button 
                        className="tutorials-close-button" 
                        onClick={() => {
                            setSearchParams({});
                            onClose();
                        }}
                    >
                        &#x00D7;
                    </button>
                </div>
            </div>

                <div className="tutorials-content">
                    {/* Sidebar navigation */}
                    <div className="tutorials-sidebar">
                        <button
                            className={`tutorials-sidebar-button ${activeTab === 'introduction' ? 'active' : ''}`}
                            onClick={() => handleTabChange('introduction')}>
                            <BookSvg className="button-icon" />
                            <span className="button-text">{t.tutorials.tabs.introduction}</span>
                        </button>
                        <button
                            className={`tutorials-sidebar-button ${activeTab === 'getting-started' ? 'active' : ''}`}
                            onClick={() => handleTabChange('getting-started')}>
                            <RocketSvg className="button-icon" />
                            <span className="button-text">{t.tutorials.tabs.gettingStarted}</span>
                        </button>
                        <button
                            className={`tutorials-sidebar-button ${activeTab === 'vs-friends' ? 'active' : ''}`}
                            onClick={() => handleTabChange('vs-friends')}>
                            <HandshakeSvg className="button-icon" />
                            <span className="button-text">{t.tutorials.tabs.vsFriends}</span>
                        </button>
                        <button
                            className={`tutorials-sidebar-button ${activeTab === 'how-to-play' ? 'active' : ''}`}
                            onClick={() => handleTabChange('how-to-play')}>
                            <GamePadSvg className="button-icon" />
                            <span className="button-text">{t.tutorials.tabs.howToPlay}</span>
                        </button>
                    </div>

                    {/* Main content area for videos */}
                    <div className="tutorials-video-content">
                        {renderTutorialVideos()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TutorialsModal;